import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { normalizeMemberPermissionsMap } from './tourMemberPermissionsService';

export const SHARE_VISIBILITY = {
  LINK: 'link',
  ACCOUNT: 'account',
};

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function generateShareToken() {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
}

export const DEFAULT_SHARE_MODULE_ORDER = [
  'VENUES',
  'TOUR PARTY',
  'RIDER',
  'STAGE',
  'SETLIST',
  'GUEST LIST',
  'SCHEDULE',
  'FILES',
  'CONTACTS',
  'MERCH',
  'NOTES',
  'DAY SHEET',
  'LODGING',
];

/** Preserve unique module labels in the caller's order. */
export function normalizeOrderedModules(modules = [], fallback = DEFAULT_SHARE_MODULE_ORDER) {
  const seen = new Set();
  const ordered = [];
  (modules || []).forEach((mod) => {
    const label = String(mod || '').trim();
    if (!label || seen.has(label)) return;
    seen.add(label);
    ordered.push(label);
  });
  if (ordered.length) return ordered;
  return [...fallback];
}

export function parseShareModulesOrderList(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  let decoded = s;
  try {
    decoded = decodeURIComponent(s.replace(/\+/g, ' '));
  } catch {
    decoded = s;
  }
  const parts = decoded
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  if (!parts.length) return null;
  return normalizeOrderedModules(parts);
}

function modulesKey(modules = []) {
  return [...modules].map((m) => String(m).trim()).filter(Boolean).sort().join('|');
}

function dayShareDocId(tourId, dateKey, modules) {
  return `day__${tourId}__${dateKey}__${modulesKey(modules).replace(/[^a-zA-Z0-9|]/g, '_')}`;
}

export function buildShareUrl(baseUrl, token) {
  const origin = String(baseUrl || 'https://venuereportcard.com').replace(/\/$/, '');
  return `${origin}/calendar/share/${encodeURIComponent(token)}`;
}

export function buildLegacyDayShareUrl({ baseUrl, ownerId, tourId, dateKey, modules }) {
  const origin = String(baseUrl || 'https://venuereportcard.com').replace(/\/$/, '');
  const mods = encodeURIComponent((modules || []).join(','));
  return `${origin}/calendar/day/${encodeURIComponent(dateKey)}?uid=${encodeURIComponent(
    ownerId
  )}&tour=${encodeURIComponent(tourId)}&modules=${mods}`;
}

export function canManageShares(userId, ownerId, managerIds = []) {
  if (!userId || !ownerId) return false;
  if (userId === ownerId) return true;
  return (managerIds || []).includes(userId);
}

export function isTourOwner(userId, ownerId) {
  return Boolean(userId && ownerId && userId === ownerId);
}

export function todayDateKey(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function shareLinkDisplayTitle(link) {
  if (!link || link.type === 'tour') return 'Full tour';
  const venue = String(link.venueName || '').trim();
  if (venue) return venue;
  const city = String(link.cityLine || '').trim();
  if (city) return city;
  return String(link.dateKey || 'Day share').trim() || 'Day share';
}

export function shareLinkPrimaryContact(link) {
  const audience = (link?.audience || []).filter((a) => a && !a.revoked);
  const first = audience[0] || null;
  if (!first) {
    return { name: '', email: '', phone: '' };
  }
  return {
    name: String(first.name || '').trim(),
    email: normalizeEmail(first.email),
    phone: String(first.phone || first.tel || '').trim(),
  };
}

function shareLinkBucket(link, todayKey) {
  if (!link || link.type === 'tour' || !link.dateKey) return 2;
  if (String(link.dateKey) >= todayKey) return 0;
  return 1;
}

export function compareShareLinksForDisplay(a, b, todayKey = todayDateKey()) {
  const ba = shareLinkBucket(a, todayKey);
  const bb = shareLinkBucket(b, todayKey);
  if (ba !== bb) return ba - bb;
  if (ba === 0) return String(a.dateKey || '').localeCompare(String(b.dateKey || ''));
  if (ba === 1) return String(b.dateKey || '').localeCompare(String(a.dateKey || ''));
  const at = a.updatedAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
  const bt = b.updatedAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
  return bt - at;
}

export function sortShareLinksForDisplay(rows, todayKey = todayDateKey()) {
  return (rows || []).slice().sort((a, b) => compareShareLinksForDisplay(a, b, todayKey));
}

/** Real-time share links for a tour. */
export function subscribeShareLinksForTour(db, ownerId, tourId, callback) {
  if (!ownerId || !tourId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, 'users', ownerId, 'calendarShareLinks'),
    where('tourId', '==', tourId)
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(sortShareLinksForDisplay(rows));
    },
    (err) => {
      console.warn('subscribeShareLinksForTour', err);
      callback([]);
    }
  );
}

/** Tour-level share manager assignments (only owner can edit). */
export function subscribeShareSettings(db, ownerId, tourId, callback) {
  if (!ownerId || !tourId) {
    callback({ managerIds: [] });
    return () => {};
  }
  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  return onSnapshot(
    ref,
    (snap) => {
      const data = snap.exists() ? snap.data() || {} : {};
      callback({
        managerIds: Array.isArray(data.managerIds) ? data.managerIds : [],
        memberPermissions: normalizeMemberPermissionsMap(data.memberPermissions),
      });
    },
    () => callback({ managerIds: [], memberPermissions: {} })
  );
}

export async function setShareManagers(db, ownerId, tourId, managerIds) {
  if (!ownerId || !tourId) throw new Error('Missing tour.');
  const ref = doc(db, 'users', ownerId, 'calendarShareSettings', tourId);
  await setDoc(
    ref,
    {
      tourId,
      managerIds: [...new Set((managerIds || []).filter(Boolean))],
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

async function writeTokenIndex(db, token, ownerId, linkId) {
  await setDoc(doc(db, 'calendarShareTokens', token), {
    ownerId,
    linkId,
    updatedAt: serverTimestamp(),
  });
}

/** Create or update a managed day share link. Returns token + url. */
export function guestListVenueLinkId(tourId, dateKey) {
  return dayShareDocId(tourId, dateKey, normalizeOrderedModules(['GUEST LIST']));
}

export function contactsDayShareLinkId(tourId, dateKey) {
  return dayShareDocId(tourId, dateKey, normalizeOrderedModules(['CONTACTS']));
}

export async function upsertDayShareLink(db, ownerId, {
  tourId,
  tourName,
  dateKey,
  modules,
  baseUrl,
  visibility = SHARE_VISIBILITY.LINK,
  label = '',
  venueName = '',
  cityLine = '',
}) {
  const mods = normalizeOrderedModules(modules);
  if (!ownerId || !tourId || !dateKey || mods.length === 0) {
    throw new Error('Tour, day, and at least one section are required.');
  }

  const linkId = dayShareDocId(tourId, dateKey, mods);
  const linkRef = doc(db, 'users', ownerId, 'calendarShareLinks', linkId);
  const existing = await getDoc(linkRef);
  const token = existing.exists() ? String(existing.data()?.token || '') : generateShareToken();
  const finalToken = token || generateShareToken();

  const payload = {
    type: 'day',
    tourId,
    tourName: String(tourName || 'Tour').trim(),
    dateKey,
    modules: mods,
    token: finalToken,
    isActive: existing.exists() ? existing.data()?.isActive !== false : true,
    visibility: existing.exists() ? existing.data()?.visibility || SHARE_VISIBILITY.LINK : visibility,
    audience: Array.isArray(existing.data()?.audience) ? existing.data().audience : [],
    ownerId,
    label: String(label || '').trim() || `${dateKey} · ${mods.slice(0, 3).join(', ')}${mods.length > 3 ? '…' : ''}`,
    updatedAt: serverTimestamp(),
    ...(existing.exists() ? {} : { createdAt: serverTimestamp() }),
  };
  const venue = String(venueName || '').trim();
  const city = String(cityLine || '').trim();
  if (venue) payload.venueName = venue;
  if (city) payload.cityLine = city;

  await setDoc(linkRef, payload, { merge: true });
  await writeTokenIndex(db, finalToken, ownerId, linkId);

  return {
    id: linkId,
    token: finalToken,
    url: buildShareUrl(baseUrl, finalToken),
    legacyUrl: buildLegacyDayShareUrl({ baseUrl, ownerId, tourId, dateKey, modules: mods }),
  };
}

export async function updateShareLink(db, ownerId, linkId, patch) {
  const ref = doc(db, 'users', ownerId, 'calendarShareLinks', linkId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Share link not found.');
  await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
}

export async function setShareLinkActive(db, ownerId, linkId, isActive) {
  await updateShareLink(db, ownerId, linkId, { isActive: Boolean(isActive) });
}

export async function setShareLinkVisibility(db, ownerId, linkId, visibility) {
  const v = visibility === SHARE_VISIBILITY.ACCOUNT ? SHARE_VISIBILITY.ACCOUNT : SHARE_VISIBILITY.LINK;
  await updateShareLink(db, ownerId, linkId, { visibility: v });
}

export async function setShareLinkModules(db, ownerId, linkId, modules) {
  const mods = normalizeOrderedModules(modules);
  if (!mods.length) throw new Error('Select at least one section.');
  await updateShareLink(db, ownerId, linkId, { modules: mods });
}

export async function setShareLinkModuleOrder(db, ownerId, linkId, modules) {
  return setShareLinkModules(db, ownerId, linkId, modules);
}

export async function addShareAudienceMember(db, ownerId, linkId, { email, userId = '', name = '', phone = '' }) {
  const emailLower = normalizeEmail(email);
  if (!emailLower) throw new Error('Email is required.');
  const ref = doc(db, 'users', ownerId, 'calendarShareLinks', linkId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Share link not found.');
  const audience = Array.isArray(snap.data()?.audience) ? snap.data().audience : [];
  const filtered = audience.filter((a) => normalizeEmail(a.email) !== emailLower);
  const entry = {
    email: emailLower,
    userId: String(userId || '').trim(),
    name: String(name || '').trim(),
    phone: String(phone || '').trim(),
    addedAt: new Date().toISOString(),
    revoked: false,
  };
  await updateDoc(ref, {
    audience: [...filtered, entry],
    updatedAt: serverTimestamp(),
  });
}

export async function revokeShareAudienceMember(db, ownerId, linkId, email) {
  const emailLower = normalizeEmail(email);
  const ref = doc(db, 'users', ownerId, 'calendarShareLinks', linkId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Share link not found.');
  const audience = (snap.data()?.audience || []).map((a) =>
    normalizeEmail(a.email) === emailLower ? { ...a, revoked: true, revokedAt: new Date().toISOString() } : a
  );
  await updateDoc(ref, { audience, updatedAt: serverTimestamp() });
}

/** Resolve token → share link (for viewers). */
export async function resolveShareByToken(db, token) {
  const t = String(token || '').trim();
  if (!t) return null;
  const indexSnap = await getDoc(doc(db, 'calendarShareTokens', t));
  if (!indexSnap.exists()) return null;
  const { ownerId, linkId } = indexSnap.data() || {};
  if (!ownerId || !linkId) return null;
  const linkSnap = await getDoc(doc(db, 'users', ownerId, 'calendarShareLinks', linkId));
  if (!linkSnap.exists()) return null;
  return {
    ownerId,
    linkId,
    link: { id: linkSnap.id, ...linkSnap.data() },
  };
}

/** Live subscription for viewers (active state, modules, audience). */
export function subscribeShareByToken(db, token, callback) {
  const t = String(token || '').trim();
  if (!t) {
    callback(null);
    return () => {};
  }
  let linkUnsub = () => {};
  const indexUnsub = onSnapshot(
    doc(db, 'calendarShareTokens', t),
    (indexSnap) => {
      linkUnsub();
      if (!indexSnap.exists()) {
        callback(null);
        return;
      }
      const { ownerId, linkId } = indexSnap.data() || {};
      if (!ownerId || !linkId) {
        callback(null);
        return;
      }
      linkUnsub = onSnapshot(
        doc(db, 'users', ownerId, 'calendarShareLinks', linkId),
        (linkSnap) => {
          if (!linkSnap.exists()) {
            callback(null);
            return;
          }
          callback({
            ownerId,
            linkId,
            link: { id: linkSnap.id, ...linkSnap.data() },
          });
        },
        () => callback(null)
      );
    },
    () => callback(null)
  );
  return () => {
    indexUnsub();
    linkUnsub();
  };
}

export function checkShareAccess(shareLink, viewer) {
  if (!shareLink) return { allowed: false, reason: 'not_found' };
  if (shareLink.isActive === false) return { allowed: false, reason: 'inactive' };
  if (shareLink.visibility !== SHARE_VISIBILITY.ACCOUNT) {
    return { allowed: true, reason: 'link' };
  }
  if (!viewer?.uid && !viewer?.email) {
    return { allowed: false, reason: 'sign_in_required' };
  }
  const emailLower = normalizeEmail(viewer.email);
  const audience = (shareLink.audience || []).filter((a) => !a.revoked);
  const match = audience.some(
    (a) =>
      (emailLower && normalizeEmail(a.email) === emailLower) ||
      (viewer.uid && a.userId && a.userId === viewer.uid)
  );
  if (match) return { allowed: true, reason: 'audience' };
  return { allowed: false, reason: 'not_invited' };
}

/** Include tour-level public snapshot from sharedTours if present. */
export async function fetchTourPublicShare(db, tourId) {
  if (!tourId) return null;
  const snap = await getDoc(doc(db, 'sharedTours', tourId));
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  return {
    type: 'tour',
    tourId,
    tourName: data.tourName || 'Tour',
    isActive: data.isPublic !== false,
    ownerId: data.ownerId || '',
    ownerName: data.ownerName || '',
    updatedAt: data.updatedAt,
    legacyUrl: typeof window !== 'undefined' ? `${window.location.origin}/tour/${tourId}` : `/tour/${tourId}`,
  };
}

export function getFirestoreDb() {
  return getFirestore();
}
